import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Partager, d'ou que l'on soit.
 *
 * # Deux defauts que ce module corrige
 *
 * **`navigator.share` n'existe pas dans une webview.** C'est une API de
 * Safari, pas du moteur embarque. Partager depuis l'application tombait
 * donc sur le repli — le presse-papiers — qui echoue lui aussi dans une
 * webview : la page annoncait « Partage impossible depuis ce
 * navigateur », sur le seul appareil ou l'on partage vraiment. Le greffon
 * `Partage.swift` ouvre la vraie feuille du systeme.
 *
 * **Le lien pointait vers `localhost`.** Capacitor sert la webview sous
 * `https://localhost`, donc `window.location.origin` designe le telephone
 * de celui qui partage. Le lien recu ne menait nulle part.
 *
 * # L'ordre
 *
 * Le greffon d'abord, la ou il existe ; `navigator.share` ensuite, pour
 * le web et pour Android, dont la webview n'a pas encore le sien ; le
 * presse-papiers en dernier, pour les navigateurs de bureau.
 */
const Partage = registerPlugin<{
  partager(options: { texte?: string; url: string }): Promise<{ partage: boolean }>;
}>('Partage');

export interface APartager {
  /** Le titre de la feuille, quand la plateforme en montre un. */
  titre?: string;
  texte?: string;
  url: string;
}

/** Ce qui s'est passe, pour que l'ecran sache quoi dire — ou se taire. */
export type IssuePartage = 'partage' | 'copie' | 'echec';

/**
 * L'adresse publique de l'application.
 *
 * Celle de l'hebergement Firebase, la meme que les courriels : un lien
 * partage doit s'ouvrir chez quelqu'un qui n'a pas Sirocco.
 */
const PUBLIQUE = 'https://siroccoapp.fr';

/** Un lien qui vaut pour tout le monde, pas seulement pour ce telephone. */
export function lienPublic(chemin: string): string {
  return `${Capacitor.isNativePlatform() ? PUBLIQUE : window.location.origin}${chemin}`;
}

/**
 * Le lien d'un post partage hors de Sirocco.
 *
 * Il ne mene pas au site mais a une page d'accueil, qui rend la main a
 * l'application **sur ce post** — `sirocco://post/<id>`. Sans elle, le
 * destinataire qui a Sirocco tombait dans un navigateur devant un mur de
 * connexion, alors que l'application etait a un geste.
 *
 * Toujours l'adresse publique, meme depuis le web : c'est un lien qu'on
 * envoie a quelqu'un d'autre, pas une adresse qu'on garde pour soi.
 */
export function lienDePost(postId: string): string {
  /* Sans l'extension : l'hebergement sert `/post` et redirige `/post.html`
     vers lui. Un lien partage ne doit pas commencer par un detour. */
  return `${PUBLIQUE}/post?id=${encodeURIComponent(postId)}`;
}

/**
 * Le lien d'un evenement partage hors de Sirocco.
 *
 * Meme porte que pour un post, et pour la meme raison : `evenement.html`
 * rend la main a l'application sur l'evenement, plutot que de laisser le
 * destinataire devant un mur de connexion.
 */
export function lienDEvenement(eventId: string): string {
  return `${PUBLIQUE}/evenement?id=${encodeURIComponent(eventId)}`;
}

export async function partager(quoi: APartager): Promise<IssuePartage> {
  if (Capacitor.isNativePlatform()) {
    try {
      await Partage.partager({ texte: quoi.texte, url: quoi.url });
      // Une feuille refermee sans rien choisir n'est pas un echec.
      return 'partage';
    } catch {
      /* Pas de greffon de ce cote — Android : on continue plus bas. */
    }
  }

  if (navigator.share) {
    try {
      await navigator.share({ title: quoi.titre, text: quoi.texte, url: quoi.url });
      return 'partage';
    } catch (cause) {
      if (cause instanceof Error && cause.name === 'AbortError') return 'partage';
    }
  }

  try {
    await navigator.clipboard.writeText(quoi.url);
    return 'copie';
  } catch {
    return 'echec';
  }
}
